"use client";

import { TypeImage, TypeImageCollection } from "@contentful/types";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { Avatar, AvatarImage } from "@yardzen/ui/components/avatar";
import { Button } from "@yardzen/ui/components/button";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPreview,
  CarouselPrevious,
} from "@yardzen/ui/components/carousel";
import { Link } from "@yardzen/ui/components/link";
import { PeekSlider } from "@yardzen/ui/components/peek-slider";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import { useEffect, useState } from "react";
import { RichText } from "./rich-text/RichText";

// Tailwind `lg` breakpoint (see libs/ui tailwind.config.js). Used to mount only
// the gallery that's actually visible — CSS `hidden` still lets next/image
// fetch, so on detail PDPs the images would otherwise download twice.
const LG_BREAKPOINT = 1200;

function useIsLgUp() {
  const [isLgUp, setIsLgUp] = useState<boolean | null>(null);
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`);
    const update = () => setIsLgUp(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);
  return isLgUp;
}

export function ImageCollectionCarousel({
  imageCollection,
  variant,
  wrapperClassName,
  headingWrapperClassName,
  headingClassName,
  slideClassName,
}: {
  imageCollection: TypeImageCollection<"WITHOUT_UNRESOLVABLE_LINKS">;
  variant?: "preview" | "detail" | "preview-small" | "modal" | undefined;
  wrapperClassName?: string;
  headingWrapperClassName?: string;
  headingClassName?: string;
  slideClassName?: string;
}) {
  const images = imageCollection.fields.images.filter(
    (img): img is TypeImage<"WITHOUT_UNRESOLVABLE_LINKS"> =>
      img !== undefined && img.sys.contentType.sys.id === "image",
  );
  const showDots = imageCollection.fields.navigationOptions?.find(
    (o) => o === "bottom-dots",
  );
  // Duplicate images if array is 4 or fewer items, until length > 4
  let displayImages = images;
  if (!showDots) {
    if (images.length > 0 && images.length <= 4) {
      while (displayImages.length <= 4) {
        displayImages = displayImages.concat(images);
      }
    }
  }
  const showArrows = imageCollection.fields.navigationOptions?.find(
    (o) => o === "left-right-arrows",
  );
  const avatar =
    imageCollection.fields.collectionImage?.fields.avatar?.fields.file?.url ||
    imageCollection.fields.collectionImage?.fields.image?.fields.file?.url;
  const avatarAlt =
    imageCollection.fields.collectionImage?.fields.label ||
    imageCollection.fields.collectionImage?.fields.description;
  const variantResolved = variant ?? "preview";
  const cta = imageCollection.fields.cta;

  // On the detail PDP the mobile PeekSlider and the desktop Carousel are shown
  // via CSS at different breakpoints, but CSS `hidden` doesn't stop next/image
  // from fetching — so mount only the one in range. `null` (pre-hydration)
  // keeps the desktop Carousel so SSR/first paint matches the common case.
  const isLgUp = useIsLgUp();
  const isDetail = variantResolved === "detail";
  const showPeekSlider = isDetail && isLgUp === false;
  const showCarousel = !isDetail || isLgUp !== false;

  const variants = cva("", {
    variants: {
      carouselClass: {
        detail: "h-full",
        preview: "h-full",
        "preview-small": "max-w-[200px] mx-auto",
        modal: "h-full",
      },
      containerClass: {
        detail: "rounded-xl",
        preview: "md:rounded-l-lg",
        "preview-small": "rounded-lg",
        modal: "rounded-xl",
      },
      imageClass: {
        detail: "rounded-xl",
        preview: "md:rounded-l-lg",
        "preview-small": "rounded-lg",
        modal: "rounded-xl",
      },
      buttonClass: {
        detail: "mt-10",
        preview: "mt-10",
        "preview-small": "mt-2",
        modal: "mt-10",
      },
    },
  });

  return (
    <div className={wrapperClassName}>
      {(avatar ||
        imageCollection.fields.heading ||
        imageCollection.fields.subheading ||
        imageCollection.fields.formattedDescription) && (
        <div className="flex items-center gap-2 pb-2">
          {avatar && (
            <Avatar className="h-[48px] w-[48px]">
              <AvatarImage
                src={avatar}
                alt={avatarAlt}
                className="h-[48px] w-[48px] object-cover object-top"
              />
            </Avatar>
          )}
          <div className={cn("flex-1", headingWrapperClassName)}>
            {imageCollection.fields.heading && (
              <h4
                className={cn(
                  "font-body text-system-grayscale-90 text-lg",
                  headingClassName,
                )}
              >
                {imageCollection.fields.heading}
              </h4>
            )}
            {imageCollection.fields.subheading && (
              <p className="text-brand-moss-greens-90 font-body text-xxs mt-1">
                <span className="mr-1 inline-block h-2 w-2 rounded-full bg-[#C9DC5C] p-0"></span>
                {imageCollection.fields.subheading}
              </p>
            )}
            {imageCollection.fields.formattedDescription && (
              <RichText
                content={imageCollection.fields.formattedDescription}
                className="text-brand-moss-greens-90 font-body m-auto mt-1"
                ulClassName="ml-7"
              />
            )}
          </div>
        </div>
      )}
      {/* Detail (PDP) below lg: no carousel chrome — shared peek gallery. */}
      {showPeekSlider && (
        <PeekSlider
          className="lg:hidden"
          fit="natural"
          slideClassName={slideClassName}
          slides={images.flatMap((image) => {
            const imageUrl = image?.fields?.image?.fields?.file?.url;
            if (!imageUrl) return [];
            return [
              {
                src: `https:${imageUrl}`,
                alt: image?.fields?.image?.fields?.description ?? "",
              },
            ];
          })}
        />
      )}
      {showCarousel && (
        <Carousel
          className={cn(
            "w-full md:mt-0",
            variantResolved === "detail" ? "max-w-none" : "max-w-[660px]",
            variants({ carouselClass: variantResolved }),
            variantResolved === "detail" && "max-lg:hidden",
          )}
          opts={{
            loop: true,
          }}
          plugins={[
            // Auto-advance; hovering a slide holds it, manual browsing stops it.
            Autoplay({
              delay: 5000,
              stopOnMouseEnter: true,
              stopOnInteraction: true,
            }),
          ]}
        >
          <div className="relative">
            <CarouselPreview
              slideCount={displayImages.length}
              className={cn(
                "overflow-hidden",
                variants({ containerClass: variantResolved }),
              )}
              renderPreview={(index) => {
                const image = displayImages[index];
                const imageUrl = image?.fields?.image?.fields?.file?.url;
                const alt = image?.fields?.image?.fields?.description;
                if (!imageUrl) return null;
                return (
                  <Image
                    src={`https:${imageUrl}`}
                    alt={alt ?? ""}
                    height={700}
                    width={700}
                    className={cn(
                      "aspect-auto max-h-[564px] w-full object-cover",
                      variants({ imageClass: variantResolved }),
                    )}
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                );
              }}
            />
            {showArrows && (
              <>
                <CarouselPrevious
                  className="left-1 top-1/2 ml-2 -translate-y-1/2 md:left-2"
                  aria-label="Previous image"
                  icon={faChevronLeft}
                />
                <CarouselNext
                  className="right-1 top-1/2 mr-2 -translate-y-1/2 md:mr-3"
                  aria-label="Next image"
                  icon={faChevronRight}
                />
              </>
            )}
          </div>

          <CarouselContent
            className={cn(
              "py-5",
              variantResolved === "modal" ? "max-h-32 sm:max-h-none" : "",
              variantResolved === "detail" || variantResolved === "modal"
                ? ""
                : "hidden",
            )}
          >
            {displayImages.map((image, index) => {
              const imageUrl = image?.fields?.image?.fields?.file?.url;
              const alt = image?.fields?.image?.fields?.description;
              if (!imageUrl) {
                return null;
              }
              return (
                <CarouselItem
                  key={index}
                  focusable
                  className={cn(
                    "h-28 basis-1/4",
                    variantResolved === "modal" ? "h-20 sm:h-28" : "",
                  )}
                >
                  <Image
                    src={`https:${imageUrl}`}
                    alt={alt ?? ""}
                    height={350}
                    width={350}
                    className="aspect-auto h-full rounded-xl object-cover"
                    loading="lazy"
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {showDots && <CarouselDots count={images.length} className="mt-2" />}
        </Carousel>
      )}
      {cta && cta.fields.buttonText && cta.fields.url && (
        <div
          className={cn(
            "flex flex-row justify-center",
            variants({ buttonClass: variantResolved }),
          )}
        >
          <Button className="w-full" asChild>
            <Link
              className="text-brand-primary-darker w-full bg-[#ECF4BF] text-xs font-semibold"
              href={cta.fields.url}
            >
              {cta?.fields.buttonText}
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
